import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../api/client'
import type { ConversationItem, Message, OrganisationAgent } from '../api/types'
import { type OnRoomFrame, useLiveMessages } from '../live/useLiveMessages'
import type { OnOrganisationFrame } from '../live/useOrganisationStream'

export interface Conversation {
  /** Oldest first; null until the first page has loaded. */
  items: ConversationItem[] | null
  failed: boolean
  /** Whether there is a page before the first one held. */
  earlier: boolean
  /** The agent's private messages you have not seen, a dot each. Room items are never in it. */
  unread: ReadonlySet<string>
  /** The oldest unread private message, or the first item when earlier pages hold more. Moves up with them. */
  newLineBefore: string | null
  /** Seen on screen: out of `unread` at once, and your read cursor moves up to the newest, never back. */
  markSeen: (ids: string[]) => void
  loadEarlier: () => void
  send: (body: string, alsoPost: boolean, replyTo?: string) => Promise<void>
  /** Answer a question the agent asked in its room, there, tagging it. */
  answerInRoom: (question: RoomItem, answer: string) => Promise<void>
  /** Start the run the agent proposed, on your default machine, which answers it with Start. */
  startProposal: (proposal: DirectItem) => Promise<void>
}

type RoomItem = Extract<ConversationItem, { place: 'room' }>
export type DirectItem = Extract<ConversationItem, { place: 'direct' }>

interface Page {
  name: string
  items: ConversationItem[]
  before: string | null
  /** How many of the agent's newest private messages the organisation stream called unread when this loaded, and have arrived since. */
  owed: number
  /** The newest cursor this tab had sent for the agent when this loaded: at or below it is read whatever `owed` says. */
  floor: string
  seen: ReadonlySet<string>
}

/**
 * The newest read cursor this tab has sent, per organisation and agent. The
 * stream's `direct_unread` is polled, so for a moment after reading it still
 * counts what was just read; a conversation opened again in that moment
 * leaves out everything at or below the cursor rather than dotting it.
 */
export const cursorsSent = new Map<string, string>()
const cursorKey = (organisation: string, name: string) => `${organisation}\n${name}`

/** Your read cursor for the agent moved up to `cursor`: sent, and kept, only when it is newer than what this tab sent. */
export function sendDirectRead(organisation: string, agent: string, cursor: string): void {
  const key = cursorKey(organisation, agent)
  if (cursor <= (cursorsSent.get(key) ?? '')) return
  cursorsSent.set(key, cursor)
  void api.markDirectRead(organisation, agent, cursor)
}

/** A proposal's first answer, `domain.PROPOSAL_ANSWERS[0]`: the one that starts its run. */
export const PROPOSAL_START = 'Start'

const fromAgent = (item: ConversationItem) => item.place === 'direct' && item.message.sender_type === 'agent'

const NOTHING_UNREAD: ReadonlySet<string> = new Set()

/**
 * The organisation stream counts the agent's private messages after your
 * cursor, which are its newest ones, so the page's newest `owed` are unread
 * (`seen` and `floor` aside). With fewer held, and none of those held at or
 * below the floor, the rest are in earlier pages and the line goes above
 * everything held, down to wherever they turn up.
 */
function unreadOf(page: Page | null): { unread: ReadonlySet<string>; newLineBefore: string | null } {
  if (!page) return { unread: NOTHING_UNREAD, newLineBefore: null }
  const theirs = page.items.filter(fromAgent).map((item) => item.message.id)
  const owed = theirs.slice(Math.max(0, theirs.length - page.owed)).filter((id) => id > page.floor)
  const more = page.owed > theirs.length && !theirs.some((id) => id <= page.floor)
  return {
    unread: new Set(owed.filter((id) => !page.seen.has(id))),
    newLineBefore: more ? (page.items[0]?.message.id ?? null) : (owed[0] ?? null),
  }
}

export interface ConversationOf {
  organisation: string
  /** The agent's full name. */
  name: string
  /** What the organisation stream says about it, while it is live or waits on you. */
  agent: OrganisationAgent | undefined
  myName: string
  onOrganisationFrame: OnOrganisationFrame
  /** The room open in the workspace, whose stream is listened to rather than opened twice. */
  openChannelId: string | null
  onOpenRoomFrame: OnRoomFrame
}

/**
 * Everything between the reader and one of their agents: the first page from
 * `/conversation`, earlier pages on request, private messages from the
 * organisation stream, and what the agent says in its room from that room's
 * stream, held only while this is open.
 */
export function useConversation({
  organisation, name, agent, myName, onOrganisationFrame, openChannelId, onOpenRoomFrame,
}: ConversationOf): Conversation {
  const [page, setPage] = useState<Page | null>(null)
  const [failed, setFailed] = useState(false)
  const fetchingBefore = useRef<string | null>(null)
  /** Whose first page has landed, and what arrived for this agent before it did. */
  const loaded = useRef<string | null>(null)
  const early = useRef<ConversationItem[]>([])
  const held = page?.name === name ? page : null
  const { unread, newLineBefore } = useMemo(() => unreadOf(held), [held])
  // What the organisation stream says is unread when the first page lands.
  const unreadCount = useRef(0)
  unreadCount.current = agent?.direct_unread ?? 0

  const append = useCallback((item: ConversationItem) => {
    if (loaded.current !== name) {
      early.current.push(item)
      return
    }
    setPage((previous) =>
      previous?.name === name && !previous.items.some((had) => had.message.id === item.message.id)
        ? { ...previous, items: [...previous.items, item], owed: previous.owed + (fromAgent(item) ? 1 : 0) }
        : previous)
  }, [name])

  useEffect(() => {
    let current = true
    loaded.current = null
    early.current = []
    setFailed(false)
    api.conversation(organisation, name).then(
      (found) => {
        if (!current) return
        loaded.current = name
        const items = [...found.items]
        let owed = unreadCount.current
        for (const item of early.current) {
          if (items.some((had) => had.message.id === item.message.id)) continue
          items.push(item)
          owed += fromAgent(item) ? 1 : 0
        }
        setPage({
          name, items, before: found.before, owed, floor: cursorsSent.get(cursorKey(organisation, name)) ?? '', seen: new Set(),
        })
      },
      () => { if (current) setFailed(true) },
    )
    return () => { current = false }
  }, [organisation, name])

  useEffect(() => onOrganisationFrame((frame) => {
    if (frame.name !== 'direct' || frame.data.agent !== name) return
    append({ place: 'direct', room: null, channel_id: null, message: frame.data })
  }), [onOrganisationFrame, name, append])

  const channelId = agent?.channel_id ?? null
  const room = agent?.repository_key ?? null
  const hear = useCallback((message: Message) => {
    const fromAgent = message.sender_type === 'agent' && message.sender === name
    const tagsAgent = message.sender_type === 'human' && message.sender === myName
      && message.mentions.includes(name.toLowerCase())
    if (channelId && room && (fromAgent || tagsAgent)) append({ place: 'room', room, channel_id: channelId, message })
  }, [channelId, room, name, myName, append])

  // The page holds the history, so a stream of its own starts after the room's
  // newest message as the server numbers it, read once the page has landed.
  const shared = channelId !== null && channelId === openChannelId
  const ownStream = held && !shared ? channelId : null
  const [start, setStart] = useState<{ channelId: string; latest: string | null } | null>(null)
  useEffect(() => {
    if (!ownStream) return
    let current = true
    api.messages(ownStream, { limit: 1 }).then(
      (found) => { if (current) setStart({ channelId: ownStream, latest: found.latest_id }) },
      () => { if (current) setFailed(true) },
    )
    return () => { current = false }
  }, [ownStream])
  const started = start?.channelId === ownStream ? start : null
  useLiveMessages(started && ownStream, started && started.latest, hear)
  useEffect(() => {
    if (!shared) return
    return onOpenRoomFrame((frame) => { if (frame.name === 'message') hear(frame.data) })
  }, [shared, onOpenRoomFrame, hear])

  const loadEarlier = useCallback(() => {
    const before = held?.before
    if (!before || fetchingBefore.current === before) return
    fetchingBefore.current = before
    api.conversation(organisation, name, before).then(
      (found) => setPage((previous) => previous?.name === name
        ? { ...previous, items: [...found.items, ...previous.items], before: found.before }
        : previous),
      // Scrolling to the top again asks again.
      () => { fetchingBefore.current = null },
    )
  }, [held?.before, organisation, name])

  const markSeen = useCallback((ids: string[]) => {
    setPage((previous) => previous?.name === name
      ? { ...previous, seen: new Set([...previous.seen, ...ids]) } : previous)
    const newest = [...ids].sort().at(-1)
    if (newest) sendDirectRead(organisation, name, newest)
  }, [organisation, name])

  const send = useCallback(async (body: string, alsoPost: boolean, replyTo?: string) => {
    const sent = await api.postDirect(organisation, { agent: name, body, sender_type: 'human', also_post: alsoPost, reply_to: replyTo })
    append({ place: 'direct', room: null, channel_id: null, message: sent })
  }, [organisation, name, append])

  const answerInRoom = useCallback(async (question: RoomItem, answer: string) => {
    const sent = await api.post(question.channel_id, { body: `@${name} ${answer}`, reply_to: question.message.id })
    append({ ...question, message: sent })
  }, [name, append])

  const startProposal = useCallback(async (proposal: DirectItem) => {
    const { id, proposed_run: run } = proposal.message
    if (!run) return
    await api.startRun(run.room, { task: run.task, proposal: { agent: name, id } })
    // The server wrote the answer; its private message arrives on the stream.
    setPage((previous) => previous?.name === name ? {
      ...previous,
      items: previous.items.map((item) => item.place === 'direct' && item.message.id === id
        ? { ...item, message: { ...item.message, answered_with: PROPOSAL_START } } : item),
    } : previous)
  }, [name])

  return {
    items: held?.items ?? null, failed, earlier: Boolean(held?.before), unread, newLineBefore, markSeen, loadEarlier,
    send, answerInRoom, startProposal,
  }
}
